import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../api/ApiRoutes";

export type AdminCollection = {
  code: string;
  name: string;
  /** TCM read-only map `{ locale: text }`; absent on older BE deploys (CASENG-1053). */
  name_translations?: Record<string, string>;
  kind: "games" | "providers";
  icon_url?: string | null;
  is_active: boolean;
  member_count: number;
  child_count: number;
  depth?: number;
  parent_code?: string | null;
  sibling_order?: number;
  default_view?: string;
  /**
   * Backend-supplied flag for system-seeded collections (e.g. `all_games`,
   * `all_providers`). Seed collections cannot be deleted — the FE hides the
   * Delete affordance entirely when this is `true`, and the BE will also
   * reject the DELETE request as a defence-in-depth.
   */
  is_seed?: boolean;
  /**
   * When true the live client should play the icon's built-in animation
   * (e.g. an animated SVG / Lottie). Defaults off — admins explicitly
   * opt in per collection via the Animate Icon toggle in the General tab.
   * Optional in the type because legacy BE responses may omit it; the FE
   * treats missing/false as "static icon".
   */
  is_animated?: boolean;
};

export type AdminCollectionsResult = {
  items: AdminCollection[];
  total: number;
  active: number;
};

function extractResult(responseBody: unknown): AdminCollectionsResult {
  if (!responseBody || typeof responseBody !== "object")
    return { items: [], total: 0, active: 0 };
  const body = responseBody as Record<string, unknown>;

  const data = body.data;
  let items: AdminCollection[] = [];

  if (data && typeof data === "object" && !Array.isArray(data)) {
    const inner = data as Record<string, unknown>;
    if (Array.isArray(inner.items)) items = inner.items as AdminCollection[];
    else if (Array.isArray(inner.collections))
      items = inner.collections as AdminCollection[];

    const total = typeof inner.total === "number" ? inner.total : undefined;
    const active = typeof inner.active === "number" ? inner.active : undefined;

    if (total !== undefined) {
      return { items, total, active: active ?? 0 };
    }
  }

  if (Array.isArray(data)) items = data as AdminCollection[];
  else if (Array.isArray(body.items)) items = body.items as AdminCollection[];

  return {
    items,
    total: items.length,
    active: items.filter((c) => c.is_active).length,
  };
}

/**
 * Shared low-level fetch for the admin Collections endpoint. Concentrates
 * the token read, instance creation, `success: false` error mapping, and
 * `extractResult` post-processing so the public-facing fetchers below only
 * need to assemble the right query params.
 */
async function fetchCollectionsRaw(
  params: Record<string, unknown>,
  errorFallback = "Failed to fetch collections",
): Promise<AdminCollectionsResult> {
  const token = JSON.parse(localStorage.getItem("token") ?? "null");
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);

  const { data: responseBody } = await instance.get(
    API_ROUTES.GET_ADMIN_COLLECTIONS,
    { params },
  );

  if (
    responseBody &&
    typeof responseBody === "object" &&
    (responseBody as Record<string, unknown>).success === false
  ) {
    throw new Error(
      ((responseBody as Record<string, unknown>).message as string) ||
        errorFallback,
    );
  }

  return extractResult(responseBody);
}

export async function fetchAdminCollections(opts?: {
  parentCode?: string;
  search?: string;
}): Promise<AdminCollectionsResult> {
  const params: Record<string, unknown> = {};
  if (opts?.parentCode) params.parent_code = opts.parentCode;
  if (opts?.search) {
    params.search = opts.search;
    params.flat = true;
  }
  return fetchCollectionsRaw(params);
}

export function useAdminCollections(opts?: {
  parentCode?: string;
  search?: string;
}) {
  const key = opts?.search
    ? [
        "casinoConfiguration",
        "collections",
        "search",
        opts?.parentCode ?? "root",
        opts.search,
      ]
    : ["casinoConfiguration", "collections", opts?.parentCode ?? "root"];
  return useQuery({
    queryKey: key,
    queryFn: () => fetchAdminCollections(opts),
    staleTime: 0,
    refetchOnMount: "always" as const,
  });
}

export async function fetchAvailableCollections(
  excludeCode: string,
  kind = "games",
  search?: string,
): Promise<AdminCollectionsResult> {
  const params: Record<string, unknown> = {
    flat: true,
    kind,
    exclude_code: excludeCode,
  };
  if (search) params.search = search;
  return fetchCollectionsRaw(params, "Failed to fetch available collections");
}

export function useAvailableCollections(
  excludeCode: string | null,
  kind = "games",
  search?: string,
) {
  return useQuery({
    queryKey: [
      "casinoConfiguration",
      "collections",
      "available",
      excludeCode,
      kind,
      search || "",
    ],
    queryFn: () => fetchAvailableCollections(excludeCode!, kind, search),
    enabled: Boolean(excludeCode),
  });
}

/**
 * Flat list of all collections of a given kind. Used by the New Collection
 * parent picker, where there is no existing code to exclude.
 */
export async function fetchCollectionsByKind(
  kind: "games" | "providers",
  search?: string,
  page?: number,
  pageSize?: number,
): Promise<AdminCollectionsResult> {
  const params: Record<string, unknown> = { flat: true, kind };
  if (search) params.search = search;
  if (typeof page === "number") params.page = page;
  if (typeof pageSize === "number") params.page_size = pageSize;
  return fetchCollectionsRaw(params);
}

/**
 * Flat list of every admin collection regardless of kind. Used by the
 * Views "Add Collection" picker where shortcuts/carousels can host both
 * games and providers collections.
 */
export async function fetchFlatAdminCollections(
  search?: string,
): Promise<AdminCollectionsResult> {
  const params: Record<string, unknown> = { flat: true };
  if (search) params.search = search;
  return fetchCollectionsRaw(params);
}

export function useFlatAdminCollections(
  search?: string,
  enabled = true,
) {
  return useQuery({
    queryKey: [
      "casinoConfiguration",
      "collections",
      "flat",
      search || "",
    ],
    queryFn: () => fetchFlatAdminCollections(search),
    enabled,
    staleTime: 30_000,
  });
}

export function useCollectionsByKind(
  kind: "games" | "providers" | null,
  search?: string,
  page?: number,
  pageSize?: number,
  enabled = true,
) {
  return useQuery({
    queryKey: [
      "casinoConfiguration",
      "collections",
      "byKind",
      kind,
      search || "",
      page ?? null,
      pageSize ?? null,
    ],
    queryFn: () =>
      fetchCollectionsByKind(
        kind as "games" | "providers",
        search,
        page,
        pageSize,
      ),
    enabled: Boolean(kind) && enabled,
  });
}

export function useNestCollection(parentCode: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (childCode: string) => {
      const token = JSON.parse(localStorage.getItem("token") ?? "null");
      const instance = createPrivateAdminPanelInstance(token?.AccessToken);
      const url = API_ROUTES.PATCH_ADMIN_COLLECTION.replace(
        "{code}",
        encodeURIComponent(childCode),
      );
      const { data } = await instance.patch(url, { parent_code: parentCode });
      if (data?.success === false) {
        throw new Error(data?.message || "Failed to nest collection");
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "collections"],
      });
    },
  });
}

export function useUnnestCollection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (childCode: string) => {
      const token = JSON.parse(localStorage.getItem("token") ?? "null");
      const instance = createPrivateAdminPanelInstance(token?.AccessToken);
      const url = API_ROUTES.PATCH_ADMIN_COLLECTION.replace(
        "{code}",
        encodeURIComponent(childCode),
      );
      const { data } = await instance.patch(url, { parent_code: null });
      if (data?.success === false) {
        throw new Error(data?.message || "Failed to unnest collection");
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "collections"],
      });
    },
  });
}
